// Import necessary types from local definition
import type { Product, ProductVariant, ProductCategory } from "./types";

// Define the mapping function locally to work with external URLs
function mapProductFromAPI(product: any): Product { 
  // Images are expected to be full URLs now
  const variants = product.variants?.map((v: any) => ({ 
      id: v.id,
      name: v.name,
      price: v.price,
      stock: v.stock,
      productId: v.product_id 
    }));
    
  // additionalImages now directly uses the stored URLs
  const additionalImages = product.images?.map((img: any) => img.image_url || '/placeholder.svg');
     
  // imageUrl now directly uses the stored URL
  const imageUrl = product.image_url || '/placeholder.svg';

  return {
    id: product.id,
    name: product.name,
    description: product.description,
    fullDescription: product.full_description || undefined,
    price: product.price,
    stock: product.stock,
    status: product.status,
    isFeatured: product.is_featured,
    isNew: product.is_new,
    discount: product.discount,
    imageUrl: imageUrl, // Use the direct URL
    categoryId: product.category_id || undefined,
    category: product.category ? { id: product.category.id, name: product.category.name } : undefined,
    variants: variants,
    additionalImages: additionalImages // Use the direct URLs
  };
}

import { supabase } from "@/integrations/supabase/client";
import { toast } from 'sonner'; 
import type {
  ProductFromAPI, 
  // CategoryFromAPI - Removed, use Category from categories.ts
  Customer as CustomerType, 
  // mapProductFromAPI is now local
  // mapCategoryFromAPI - Removed, use mapping in categories.ts
} from "./types"; 
import type { Database } from "@/integrations/supabase/types"; 

type ProductInsert = Database['public']['Tables']['products']['Insert'];
type ProductUpdate = Database['public']['Tables']['products']['Update'];
// Removed CategoryInsert/Update types, use types from categories.ts
type ProductImageInsert = Database['public']['Tables']['product_images']['Insert'];

// Adjusted CustomerInsert/Update types to OMIT the non-existent columns
// and correctly include 'cpf' if it's not in the generated base type yet.
type BaseCustomerInsert = Database['public']['Tables']['customers']['Insert'];
type BaseCustomerUpdate = Database['public']['Tables']['customers']['Update'];

type CustomerInsert = Omit<BaseCustomerInsert, 'city' | 'state' | 'postal_code'> & { 
  cpf?: string | null; 
  name: string;
  phone: string;
  email?: string | null;
  address?: string | null;
  customer_type?: string | null;
  notes?: string | null;
};
type CustomerUpdate = Omit<BaseCustomerUpdate, 'city' | 'state' | 'postal_code'> & { 
  cpf?: string | null; 
  name?: string | null;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
  customer_type?: string | null;
  notes?: string | null;
};


type SystemSettingsUpdate = Database['public']['Tables']['system_settings']['Update'];
type SystemSettingsInsert = Database['public']['Tables']['system_settings']['Insert'];

// Updated FormData type to expect URLs for images
interface ProductFormData {
  name: string;
  description: string;
  full_description?: string;
  price: string; 
  stock: string; 
  category_id: string | null; 
  featured: boolean;
  new: boolean;
  status: boolean;
  image_url?: string | null; // Expects a full URL or null/empty
  additional_images?: string[]; 
  images_to_delete?: string[]; 
}

const parseNumberOrNull = (value: any): number | null => {
  if (value === null || value === undefined || String(value).trim() === '') return null;
  const stringValue = String(value).replace(',', '.'); 
  const num = Number(stringValue);
  return isNaN(num) ? null : num; 
}

// Basic URL validation (can be improved)
const isValidUrl = (urlString: string | null | undefined): boolean => {
  if (!urlString) return false;
  try {
    new URL(urlString);
    return true;
  } catch (_) {
    return false;
  }
};

// --- PRODUCT CRUD (Adapted for External URLs) --- 

export const fetchProducts = async (filters?: {
  category?: string | null,
  search?: string,
  featured?: boolean,
  new?: boolean,
  status?: boolean, 
  includeInactive?: boolean 
  exclude?: string,
}): Promise<ProductFromAPI[]> => { 
  let query = supabase
    .from('products')
    .select('*, category:category_id(id, name), images:product_images(id, image_url)'); 

  if (filters?.includeInactive !== true) {
    query = query.eq('status', filters?.status ?? true); 
  }
    if (filters?.category !== undefined) { 
    if (filters.category === null || filters.category === '') {
      query = query.is('category_id', null);
    } else {
      const isValidUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(filters.category);
      if (isValidUUID) {
          query = query.eq('category_id', filters.category);
      } else {
          console.warn(`Category filter ignored: Invalid UUID format - ${filters.category}`);
      }
    }
  }
  if (filters?.search) {
    const searchTerm = `%${filters.search}%`;
    query = query.or(`name.ilike.${searchTerm},description.ilike.${searchTerm}`);
  }
  if (filters?.featured !== undefined) {
    query = query.eq('is_featured', filters.featured);
  }
  if (filters?.new !== undefined) {
    query = query.eq('is_new', filters.new);
  }
  if (filters?.exclude) {
    query = query.neq('id', filters.exclude);
  }

  const { data, error } = await query.order('name', { ascending: true });

  if (error) {
    console.error("Error fetching products:", error);
    throw new Error(`Falha ao buscar produtos: ${error.message}`);
  }
  return (data as ProductFromAPI[]) || []; 
};

export const fetchFeaturedProducts = async (limit: number = 6) => {
  try {
    const data = await fetchProducts({ featured: true, status: true }); 
    return data.slice(0, limit).map(mapProductFromAPI); // Use local mapper
  } catch (error: any) {
    console.error("Error fetching featured products:", error);
    throw new Error(`Falha ao buscar produtos em destaque.`);
  }
};

export const fetchNewProducts = async (limit: number = 6) => {
  try {
    const data = await fetchProducts({ new: true, status: true }); 
    return data.slice(0, limit).map(mapProductFromAPI); // Use local mapper
  } catch (error: any) {
    console.error("Error fetching new products:", error);
    throw new Error(`Falha ao buscar novos produtos.`);
  }
};

export const fetchProductById = async (id: string): Promise<ProductFromAPI | null> => {
  if (!id) return null;
  const { data, error } = await supabase
    .from('products')
    .select(`*, category:category_id(id, name), images:product_images(id, image_url, sort_order) `)
    .eq('id', id)
    .maybeSingle(); 

  if (error) {
    console.error(`Error fetching product ${id}:`, error);
    if (error.code === 'PGRST116') return null;
    throw new Error(`Falha ao buscar detalhes do produto: ${error.message}`);
  }
   if (data?.images) {
    data.images.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  }
  return data as ProductFromAPI | null;
};

// Create Product (Adapted for external URLs)
export const createProduct = async (formData: ProductFormData) => {
  console.log("API: createProduct called with formData (external URLs):", formData);
  try {
    const price = parseNumberOrNull(formData.price);
    const stock = parseNumberOrNull(formData.stock);
    const discount = parseNumberOrNull(formData.discount);

    if (price === null || price < 0) throw new Error("Preço inválido.");
    if (stock === null || stock < 0) throw new Error("Estoque inválido.");
    if (discount !== null && (discount < 0 || discount > 100)) throw new Error("Desconto inválido.");
    if (formData.image_url && !isValidUrl(formData.image_url)) {
        console.warn("URL da imagem principal pode ser inválida:", formData.image_url)
    }

    const productData: ProductInsert = {
      name: formData.name,
      description: formData.description,
      price: price,
      stock: stock,
      category_id: formData.category_id || null, 
      is_featured: formData.featured ?? false,
      is_new: formData.new ?? false,
      status: formData.status === true, 
      image_url: formData.image_url || null, 
      discount: discount 
    };
    
    console.log("API: Inserting product data (external URLs):", productData);

    const { data: newProduct, error: productError } = await supabase
      .from('products')
      .insert(productData)
      .select()
      .single();

    if (productError) {
      console.error("API: Supabase product insert error:", productError);
      throw new Error(`Erro ao salvar produto: ${productError.message}`);
    }
    if (!newProduct) throw new Error("Falha ao criar produto: resposta vazia do banco.");
    
    console.log("API: Product created with ID:", newProduct.id);

    if (formData.additional_images && formData.additional_images.length > 0) {
      const validImageUrls = formData.additional_images.filter(isValidUrl);
      if(validImageUrls.length !== formData.additional_images.length){
          console.warn("Algumas URLs de imagens adicionais são inválidas e foram ignoradas.");
          toast.warning("Algumas URLs de imagens adicionais são inválidas e foram ignoradas.");
      }
      
      if(validImageUrls.length > 0) {
          const additionalImagesData: ProductImageInsert[] = validImageUrls.map((url, index) => ({
            product_id: newProduct.id,
            image_url: url, 
            sort_order: index 
          }));
          
          console.log("API: Inserting additional image URLs:", additionalImagesData);
    
          const { error: addImagesError } = await supabase
            .from('product_images')
            .insert(additionalImagesData);
    
          if (addImagesError) {
            console.error("API: Error adding additional image URLs:", addImagesError);
            toast.warning(`Produto criado, mas falha ao salvar links de imagens adicionais: ${addImagesError.message}`);
          }
      } else {
          console.log("API: No valid additional image URLs to insert.");
      }
    }
    return newProduct;

  } catch (error) {
    console.error("API: Error in createProduct function:", error);
    throw error instanceof Error ? error : new Error(String(error || "Erro desconhecido ao criar produto.")); 
  }
};

// Update Product (Adapted for external URLs)
export const updateProduct = async ({ id, data: formData }: { id: string, data: ProductFormData }) => {
   console.log(`API: updateProduct called for ID: ${id} with formData (external URLs):`, formData);
  try {
    const price = parseNumberOrNull(formData.price);
    const stock = parseNumberOrNull(formData.stock);
    const discount = parseNumberOrNull(formData.discount);

    if (price === null || price < 0) throw new Error("Preço inválido.");
    if (stock === null || stock < 0) throw new Error("Estoque inválido.");
    if (discount !== null && (discount < 0 || discount > 100)) throw new Error("Desconto inválido.");
     if (formData.image_url && !isValidUrl(formData.image_url)) {
        console.warn("URL da imagem principal pode ser inválida:", formData.image_url)
    }

    const productUpdateData: ProductUpdate = {
      name: formData.name,
      description: formData.description,
      price: price,
      stock: stock,
      category_id: formData.category_id || null,
      is_featured: formData.featured ?? false,
      is_new: formData.new ?? false,
      status: formData.status === true, 
      discount: discount,
      image_url: formData.image_url || null 
    };
    
    console.log("API: Updating product data (external URLs):", productUpdateData);

    const { data: updatedProduct, error: productUpdateError } = await supabase
      .from('products')
      .update(productUpdateData)
      .eq('id', id)
      .select()
      .single();

    if (productUpdateError) {
      console.error("API: Supabase product update error:", productUpdateError);
      throw new Error(`Erro ao atualizar produto: ${productUpdateError.message}`);
    }
    if (!updatedProduct) throw new Error("Falha ao atualizar produto: resposta vazia do banco.");

    console.log("API: Product updated successfully.");

    // --- Handle changes in additional image URLs --- 
    if (formData.images_to_delete && formData.images_to_delete.length > 0) {
      console.log("API: Deleting image entries with IDs:", formData.images_to_delete);
      const { error: deleteError } = await supabase
        .from('product_images')
        .delete()
        .in('id', formData.images_to_delete);

      if (deleteError) {
        console.error("API: Error deleting image entries:", deleteError);
        toast.warning(`Produto atualizado, mas falha ao remover links de imagens antigas: ${deleteError.message}`);
      }
    }

    if (formData.additional_images && formData.additional_images.length > 0) {
       const validImageUrls = formData.additional_images.filter(isValidUrl);
        if(validImageUrls.length !== formData.additional_images.length){
            console.warn("Algumas URLs de imagens adicionais são inválidas e foram ignoradas.");
            toast.warning("Algumas URLs de imagens adicionais são inválidas e foram ignoradas.");
        }
        
       if(validImageUrls.length > 0) {
          const { data: existingImages, error: fetchExistingError } = await supabase
             .from('product_images')
             .select('sort_order')
             .eq('product_id', id)
             .order('sort_order', { ascending: false })
             .limit(1);
    
           if (fetchExistingError) {
               console.error("API: Error fetching existing image sort order:", fetchExistingError);
           }
           const startSortOrder = existingImages?.[0]?.sort_order !== undefined ? existingImages[0].sort_order + 1 : 0;
    
          const additionalImagesData: ProductImageInsert[] = validImageUrls.map((url, index) => ({
            product_id: id, 
            image_url: url, 
            sort_order: startSortOrder + index
          }));
          
          console.log("API: Inserting new additional image URLs:", additionalImagesData);
    
          const { error: addImagesError } = await supabase
            .from('product_images')
            .insert(additionalImagesData);
    
          if (addImagesError) {
            console.error("API: Error adding additional image URLs:", addImagesError);
            toast.warning(`Produto atualizado, mas falha ao salvar novos links de imagens: ${addImagesError.message}`);
          }
       } else {
           console.log("API: No valid new additional image URLs to insert.")
       }
    }
    return updatedProduct;

  } catch (error) {
     console.error("API: Error in updateProduct function:", error);
     throw error instanceof Error ? error : new Error(String(error || "Erro desconhecido ao atualizar produto."));
  }
};

// --- Categories --- (REMOVED)
// export const fetchCategories = ... 
// export const createCategory = ... 
// export const updateCategory = ... 
// export const deleteCategory = ... 

// --- Orders --- 
export const fetchOrders = async (status?: string) => {
  let query = supabase
    .from('orders')
    .select(`* , items:order_items(*, product:products(name), variant:product_variants(name)) `);
  if (status) query = query.eq('status', status);
  const { data, error } = await query.order('created_at', { ascending: false });
  if (error) { console.error("Error fetching orders:", error); throw new Error(`Failed to fetch orders: ${error.message}`); }
  const transformedData = data?.map((order: any) => ({
    ...order,
    items: order.items.map((item: any) => ({
      ...item,
      product_name: item.product?.name || 'N/A',
      variant_name: item.variant?.name || null
    }))
  })) || [];
  return transformedData;
};
export const fetchOrderById = async (id: string) => {
  const { data, error } = await supabase
    .from('orders')
    .select(`*, items:order_items(*, product:products(name), variant:product_variants(name)) `)
    .eq('id', id)
    .maybeSingle();
  if (error) { console.error(`Error fetching order ${id}:`, error); if (error.code === 'PGRST116') return null; throw new Error(`Failed to fetch order: ${error.message}`); }
  if (!data) return null;
  const transformedData = {
    ...data,
    items: data.items.map((item: any) => ({
      ...item,
      product_name: item.product?.name || 'N/A',
      variant_name: item.variant?.name || null
    }))
  };
  return transformedData;
};
export const updateOrderStatus = async (orderId: string, status: string) => {
  const { data, error } = await supabase.from('orders').update({ status }).eq('id', orderId).select().single();
  if (error) { console.error("Error updating order status:", error); throw new Error(`Failed to update order status: ${error.message}`); }
  return data;
};

// --- Dashboard --- 
export const fetchDashboardStats = async () => {
  try {
    const productCountPromise = supabase.from('products').select('*', { count: 'exact', head: true });
    const orderCountPromise = supabase.from('orders').select('*', { count: 'exact', head: true });
    const recentOrdersPromise = supabase.from('orders').select('*').order('created_at', { ascending: false }).limit(5);
    const [{ count: productCount, error: productError }, { count: orderCount, error: orderError }, { data: recentOrders, error: recentOrdersError }] = await Promise.all([productCountPromise, orderCountPromise, recentOrdersPromise]);
    if (productError || orderError || recentOrdersError) { throw productError || orderError || recentOrdersError; }
    return { productCount: productCount ?? 0, orderCount: orderCount ?? 0, recentOrders: recentOrders ?? [] };
  } catch (error: any) {
    console.error("Error fetching dashboard stats:", error);
    throw new Error(`Failed to fetch dashboard stats: ${error.message}`);
  }
};

// --- System Settings --- 
export const fetchSystemSettings = async () => {
  const { data, error } = await supabase.from('system_settings').select('*').limit(1).maybeSingle();
  if (error) { console.error("Error fetching system settings:", error); throw new Error(`Failed to fetch system settings: ${error.message}`); }
  return data || null;
};
export const updateSystemSettings = async (settings: SystemSettingsUpdate | SystemSettingsInsert) => {
  try {
    const { data: existing, error: fetchError } = await supabase.from('system_settings').select('id').limit(1).maybeSingle();
    if (fetchError) { throw new Error(`Failed to check existing settings: ${fetchError.message}`); }
    let result;
    if (existing) {
      const { id, ...updateData } = settings as SystemSettingsUpdate & { id?: number };
      const { data, error } = await supabase.from('system_settings').update(updateData).eq('id', existing.id).select().maybeSingle();
      if (error) throw error;
      result = data;
    } else {
      const { id, ...insertData } = settings as SystemSettingsInsert & { id?: number };
      const { data, error } = await supabase.from('system_settings').insert(insertData).select().maybeSingle();
      if (error) throw error;
      result = data;
    }
    return result;
  } catch (error: any) {
    console.error("Error in updateSystemSettings:", error);
    throw new Error(`Failed to update system settings: ${error.message}`);
  }
};

// --- Customers --- 
export const findCustomerByPhone = async (phone: string): Promise<CustomerType | null> => {
  if (!phone) return null;
  try {
    const { data, error } = await supabase.from('customers')
        .select('id, name, phone, email, address, customer_type, notes, cpf') 
        .eq('phone', phone).maybeSingle(); 
    if (error) throw error;
    return data as CustomerType | null;
  } catch (error) {
    console.error("Erro ao buscar cliente por telefone:", error);
    return null;
  }
};

export const getCustomerById = async (id: string): Promise<CustomerType | null> => {
  if (!id) return null;
  try {
    const selectColumns = 'id, name, phone, email, address, customer_type, notes, cpf';
    console.log(`API: Fetching customer by ID: ${id} with select: ${selectColumns}`); 

    const { data, error } = await supabase.from('customers')
        .select(selectColumns) 
        .eq('id', id)
        .maybeSingle();
        
    if (error) { 
      console.error('Erro detalhado ao buscar cliente por ID:', error); 
      if (error.code === 'PGRST116') return null; 
      throw error; 
    }
    console.log(`API: getCustomerById - ID: ${id}, Data Fetched:`, data); 
    return data as CustomerType | null;
  } catch (error) {
    console.error('Erro ao buscar cliente por ID:', error);
    throw new Error(`Failed to get customer by ID: ${error instanceof Error ? error.message : String(error)}`);
  }
};

export const createCustomer = async (customerData: CustomerInsert): Promise<CustomerType> => {
  console.log("API: Creating customer with raw data:", customerData); 
  try {
    const cleanCustomerData: CustomerInsert = {
      name: customerData.name,
      phone: customerData.phone,
      email: customerData.email || null,
      address: customerData.address || null,
      customer_type: customerData.customer_type || null,
      notes: customerData.notes || null,
      cpf: customerData.cpf ? String(customerData.cpf).replace(/\D/g, '') : null,
    };
    if (cleanCustomerData.cpf === '') cleanCustomerData.cpf = null; 

    const existingCustomer = await findCustomerByPhone(cleanCustomerData.phone || '');
    if (existingCustomer) {
      console.warn("API: Customer with this phone already exists, attempting update instead.");
      const { id, created_at, last_order_date, ...updateDataFromForm } = customerData as any; 
      
      const validUpdateData: CustomerUpdate = {
          name: updateDataFromForm.name,
          phone: updateDataFromForm.phone,
          email: updateDataFromForm.email || null,
          address: updateDataFromForm.address || null,
          customer_type: updateDataFromForm.customer_type || null,
          notes: updateDataFromForm.notes || null,
          cpf: updateDataFromForm.cpf ? String(updateDataFromForm.cpf).replace(/\D/g, '') : null,
      };
       if (validUpdateData.cpf === '') validUpdateData.cpf = null; 

       const existingComparableData: Partial<CustomerType> = {
         name: existingCustomer.name,
         phone: existingCustomer.phone,
         email: existingCustomer.email || null,
         address: existingCustomer.address || null,
         customer_type: existingCustomer.customer_type || null,
         notes: existingCustomer.notes || null,
         cpf: existingCustomer.cpf || null,
       };

      if (Object.keys(validUpdateData).some(key => 
          (validUpdateData as any)[key] !== (existingComparableData as any)[key] && 
          key !== 'phone' 
      )) { 
        console.log("API: Changes detected, proceeding with update.");
        return updateCustomer(existingCustomer.id, validUpdateData); 
      } else {
        console.log("API: No new data to update for existing customer by phone.");
        return existingCustomer; 
      }
    }
    
    console.log("API: Inserting new customer:", cleanCustomerData);
    const { data, error } = await supabase.from('customers').insert([cleanCustomerData]).select().single();
    if (error) {
        console.error("API: Supabase insert error:", error);
        throw error;
    }
    if (!data) throw new Error("Customer creation returned null");
    console.log("API: Customer created successfully:", data);
    return data as CustomerType;
  } catch (error) {
    console.error('Erro ao criar cliente:', error);
    throw new Error(`Failed to create customer: ${error instanceof Error ? error.message : String(error)}`);
  }
};

export const updateCustomer = async (id: string, customerData: CustomerUpdate): Promise<CustomerType> => {
  console.log(`API: Updating customer ${id} with raw data:`, customerData); 
  try {
    const cleanCustomerData: CustomerUpdate = {
        name: customerData.name,
        phone: customerData.phone,
        email: customerData.email || null,
        address: customerData.address || null,
        customer_type: customerData.customer_type || null,
        notes: customerData.notes || null,
        cpf: customerData.cpf ? String(customerData.cpf).replace(/\D/g, '') : null,
    };
     if (cleanCustomerData.cpf === '') cleanCustomerData.cpf = null; 

    Object.keys(cleanCustomerData).forEach(key => (cleanCustomerData as any)[key] === undefined && delete (cleanCustomerData as any)[key]);

    console.log(`API: Updating customer ${id} with cleaned/validated data:`, cleanCustomerData);
    
    const { data, error } = await supabase.from('customers')
      .update(cleanCustomerData)
      .eq('id', id)
      .select()
      .single();
      
    if (error) {
        console.error("API: Supabase update error:", error);
        throw error;
    }
    if (!data) throw new Error("Customer update returned null");
    console.log("API: Customer updated successfully:", data);
    return data as CustomerType;
  } catch (error) {
    console.error('Erro ao atualizar cliente:', error);
     throw new Error(`Failed to update customer: ${error instanceof Error ? error.message : String(error)}`);
  }
};


export const fetchCustomers = async (search?: string): Promise<CustomerType[]> => {
  try {
    const selectColumns = 'id, name, phone, email, address, customer_type, notes, cpf';
    let query = supabase.from('customers').select(selectColumns); 

    if (search) {
      const normalizedSearch = search.replace(/\D/g, '');
      if ((normalizedSearch.length === 11 || normalizedSearch.length === 14) && normalizedSearch === search.replace(/[.\-\/]/g, '')) {
         console.log(`API: Searching customers by CPF/CNPJ: ${normalizedSearch}`);
         query = query.eq('cpf', normalizedSearch); 
      } else {
         console.log(`API: Searching customers by name/phone/email: ${search}`);
         query = query.or(`name.ilike.%${search}%,phone.ilike.%${search}%,email.ilike.%${search}%`);
      }
    }
    
    const { data, error } = await query.order('name');
    if (error) throw error;
    return (data as CustomerType[]) || [];
  } catch (error) {
    console.error('Erro ao buscar clientes:', error);
    return [];
  }
};

export const deleteCustomer = async (id: string): Promise<void> => {
  try {
    const { error } = await supabase.from('customers').delete().eq('id', id);
    if (error) throw error;
  } catch (error) {
    console.error('Erro ao excluir cliente:', error);
    throw new Error(`Failed to delete customer: ${error instanceof Error ? error.message : String(error)}`);
  }
};

